/**
 * FILE: packages\supabase\src\services\chatbotAdmin.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { ServiceError } from '../internal/errors'
import { logActivity, withActivityActor, type ActivityActor } from '../internal/activity'
import {
  getConversations,
  getMessages,
  sendMessage,
  resolveConversation,
  type SupportConversationListItem,
  type SupportMessageRow,
  type SupportConversationStatus,
  type SupportConversationCategory,
} from './supportChat'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

export interface FaqArticleRow {
  id: string
  question: string
  answer: string
  keywords: string | null
  pattern: string | null
  category: string | null
  priority: number
  enabled: boolean
  article_type: 'faq' | 'markdown'
  title: string | null
  content: string | null
  content_text: string | null
  tags: string[] | null
  created_at: string
  updated_at: string
}

export function getArticleDisplay(row: FaqArticleRow): { title: string; body: string; keywords: string } {
  if (row.article_type === 'markdown') {
    return {
      title: row.title || row.question || 'Untitled article',
      body: row.content_text || row.content || '',
      keywords: (row.tags ?? []).join(', ') || row.keywords || '',
    }
  }
  return {
    title: row.question,
    body: row.answer,
    keywords: row.keywords ?? '',
  }
}

export interface ChatbotTicketItem extends SupportConversationListItem {
  moodle_user_id?: string | null
  hub_email?: string | null
  hub_course?: string | null
}

const CHATBOT_SECRET = import.meta.env.VITE_MOODLE_CHATBOT_SECRET

async function callChatbotEdge(action: string, payload: Record<string, unknown> = {}) {
  if (!CHATBOT_SECRET) {
    throw new ServiceError('VITE_MOODLE_CHATBOT_SECRET is not configured', 'CHATBOT_SECRET_MISSING')
  }
  if (!import.meta.env.VITE_SUPABASE_URL) {
    throw new ServiceError('VITE_SUPABASE_URL is not configured', 'CHATBOT_URL_MISSING')
  }
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/student-chatbot`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CHATBOT_SECRET}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const data = await res.json()
  if (!res.ok) {
    throw new ServiceError(data.error ?? 'Chatbot request failed', 'CHATBOT_EDGE')
  }
  return data
}

export async function getChatbotTickets(limit = 100, client: DbClient = defaultDbClient): Promise<ChatbotTicketItem[]> {
  const { data, error } = await client
    .from('support_conversations')
    .select('*, assigned_staff:staff!support_conversations_assigned_to_fkey(name)')
    .contains('metadata', { source: 'chatbot' })
    .order('last_message_at', { ascending: false })
    .limit(limit)

  if (error) throw new ServiceError(error.message, 'CHATBOT_TICKETS_FETCH', error)

  return (data ?? []).map((c) => {
    const meta = (c.metadata ?? {}) as Record<string, string | null>
    return {
      id: c.id,
      phone: c.phone,
      assigned_to: c.assigned_to,
      assigned_staff_name: c.assigned_staff?.name ?? null,
      status: c.status as SupportConversationStatus,
      category: (c.category as SupportConversationCategory) ?? 'support',
      last_message_at: c.last_message_at ?? c.created_at ?? new Date().toISOString(),
      unread_count: c.unread_count ?? 0,
      created_at: c.created_at ?? new Date().toISOString(),
      student_name: meta.fullname ?? 'Moodle student',
      course_code: meta.course ?? '',
      program: '',
      valid_till: null,
      attempt: null,
      last_preview: c.last_message_preview ?? '',
      moodle_user_id: meta.moodle_user_id ?? null,
      hub_email: meta.email ?? null,
      hub_course: meta.course ?? null,
    }
  })
}

export async function getChatbotMessages(conversationId: string, page = 0) {
  return getMessages(conversationId, page)
}

export async function sendChatbotReply(
  conversationId: string,
  content: string,
  staffId: string,
  actor: ActivityActor,
): Promise<SupportMessageRow> {
  return sendMessage(conversationId, content, 'staff', staffId, actor)
}

export async function resolveChatbotTicket(conversationId: string, actor: ActivityActor): Promise<void> {
  await resolveConversation(conversationId, actor)
  await sendMessage(conversationId, 'This conversation has been marked as resolved.', 'system', 'system', actor)
}

export async function listFaqArticles(search?: string, client: DbClient = defaultDbClient): Promise<FaqArticleRow[]> {
  let query = client.from('faq_articles').select('*').order('priority', { ascending: false })
  if (search?.trim()) {
    const term = search.trim()
    query = query.or(
      `question.ilike.%${term}%,keywords.ilike.%${term}%,title.ilike.%${term}%,content_text.ilike.%${term}%`,
    )
  }
  const { data, error } = await query
  if (error) throw new ServiceError(error.message, 'FAQ_LIST', error)
  return (data ?? []) as FaqArticleRow[]
}

export async function createFaqArticle(
  payload: Omit<FaqArticleRow, 'id' | 'created_at' | 'updated_at'>,
  actor: ActivityActor, client: DbClient = defaultDbClient,
): Promise<FaqArticleRow> {
  const { data, error } = await client
    .from('faq_articles')
    .insert({ ...payload, tags: payload.tags ?? undefined, created_by: actor.userId, updated_at: new Date().toISOString() })
    .select()
    .single()
  if (error || !data) throw new ServiceError(error?.message ?? 'Insert failed', 'FAQ_CREATE', error)
  await logActivity(withActivityActor(actor, { action: 'CREATE_CHATBOT_FAQ', entityType: 'faq_articles', entityId: data.id }), client)
  return data as FaqArticleRow
}

export async function updateFaqArticle(
  id: string,
  payload: Partial<FaqArticleRow>,
  actor: ActivityActor, client: DbClient = defaultDbClient,
): Promise<FaqArticleRow> {
  const { data, error } = await client
    .from('faq_articles')
    .update({ ...payload, tags: payload.tags ?? undefined, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  if (error || !data) throw new ServiceError(error?.message ?? 'Update failed', 'FAQ_UPDATE', error)
  await logActivity(withActivityActor(actor, { action: 'UPDATE_CHATBOT_FAQ', entityType: 'faq_articles', entityId: id }), client)
  return data as FaqArticleRow
}

export async function deleteFaqArticle(id: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client.from('faq_articles').delete().eq('id', id)
  if (error) throw new ServiceError(error.message, 'FAQ_DELETE', error)
  await logActivity(withActivityActor(actor, { action: 'DELETE_CHATBOT_FAQ', entityType: 'faq_articles', entityId: id }), client)
}

export async function createFaqArticlesBulk(
  payloads: Omit<FaqArticleRow, 'id' | 'created_at' | 'updated_at'>[],
  actor: ActivityActor, client: DbClient = defaultDbClient,
): Promise<FaqArticleRow[]> {
  if (!payloads.length) return []
  const now = new Date().toISOString()
  const rows = payloads.map((p) => ({
    ...p,
    tags: p.tags ?? undefined,
    created_by: actor.userId,
    updated_at: now,
  }))
  const { data, error } = await client.from('faq_articles').insert(rows).select()
  if (error) throw new ServiceError(error.message, 'FAQ_BULK_CREATE', error)
  const created = (data ?? []) as FaqArticleRow[]
  await logActivity(
    withActivityActor(actor, {
      action: 'BULK_CREATE_CHATBOT_FAQ',
      entityType: 'faq_articles',
      entityId: 'bulk',
      newValues: { count: created.length, ids: created.slice(0, 50).map((f) => f.id) },
    }), client
  )
  return created
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function rowToCsv(row: Record<string, string>): string {
  return Object.values(row).map(escapeCsvCell).join(',')
}

export async function exportFaqArticles(format: 'json' | 'csv', client: DbClient = defaultDbClient): Promise<string> {
  const { data, error } = await client.from('faq_articles').select('*').order('priority', { ascending: false })
  if (error) throw new ServiceError(error.message, 'FAQ_EXPORT', error)
  const rows = (data ?? []) as FaqArticleRow[]

  if (format === 'csv') {
    const header = ['article_type', 'question', 'answer', 'title', 'content', 'content_text', 'tags', 'keywords', 'pattern', 'category', 'priority', 'enabled']
    const lines = [header.join(','), ...rows.map((r) =>
      rowToCsv({
        article_type: r.article_type ?? 'faq',
        question: r.question,
        answer: r.answer,
        title: r.title ?? '',
        content: r.content ?? '',
        content_text: r.content_text ?? '',
        tags: (r.tags ?? []).join('|'),
        keywords: r.keywords ?? '',
        pattern: r.pattern ?? '',
        category: r.category ?? '',
        priority: String(r.priority),
        enabled: r.enabled ? 'true' : 'false',
      }),
    )]
    return lines.join('\n')
  }

  return JSON.stringify(rows, null, 2)
}

export async function testChatbotQuestion(question: string) {
  return callChatbotEdge('ask', { question, moodle_user_id: 0, sessionid: 'hub-test' })
}

export async function fetchMissedQueries(since: string, limit = 50) {
  return callChatbotEdge('missed_queries', { since, limit })
}

export async function fetchPersistentUsers(since: string, minCount = 2) {
  return callChatbotEdge('persistent_users', { since, min_count: minCount })
}

export async function fetchQueryStats(since: string) {
  return callChatbotEdge('query_stats', { since })
}

/** Re-export for non-chatbot-filtered inbox if needed */
export { getConversations }
